import { CellClickedEvent } from '@ag-grid-community/core';

import { CallableOrderItem } from '../../../../ecall-off/models/ecall-off-model';
import { AddToCartHeaderComponent }
	from '../../../../offer/components/utils/product-table/header-def/add-to-cart-header.component';
import { ECALL_BY_VOLUME_DATACENTER_TYPE, UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { DimensionPipe } from '../../../pipes/format-dimension.pipe';
import { TranslationService } from '../../../services/translation.service';
import {
	CommonColDef, IconMethodColDef, NumericColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterDateRangeFilterComponent } from '../../filter-type/datacenter-date-range-filter.component';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterOrderNumberFilterComponent } from '../../filter-type/datacenter-order-number-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { CheckboxHeaderComponent } from '../../header-components/checkbox-header.component';
import { DatacenterSuggestiveListFilterComponent } from '../../filter-type/datacenter-suggestive-list-filter.component';
import { ORDER_SERVER_URL } from '../../../../config';

const GET_CALLABLE_BATCHES_URL = `${ ORDER_SERVER_URL }/v5/datacenterBatches`;
export class EcallByVolumeDatacenterColumnDefinition {
	readonly REQUIRED_FIELDS = [];

	readonly DEFAULT_CONFIGURATION: Map<string, CommonColDef<CallableOrderItem>>;

	constructor(
		private readonly dimensionPipe: DimensionPipe,
		private readonly translationService: TranslationService
	) {
		this.DEFAULT_CONFIGURATION = new Map<string, CommonColDef<CallableOrderItem>>([
			[
				'add',
				new CommonColDef<CallableOrderItem>({
					colId: 'add',
					filter: false,
					isMandatoryOnLeft: true,
					sortable: false,
					headerName: this.translationService.getTR('product.table.header.add'),
					minWidth: 40,
					maxWidth: 40,
					width: 40,
					headerComponentParams: {
						showCheckbox: true,
						icon_checked: 'add_to_cart',
						icon_unchecked: 'add_to_cart_blue',
						icon_indeterminate: 'add_to_cart_grey'
					},
					headerComponent: AddToCartHeaderComponent,
					checkboxSelection: true,
					pinned: 'left'
				})
			],
			[
				'cdd',
				new CommonColDef<CallableOrderItem>({
					colId: 'cdd',
					field: 'cdd',
					filter: DatacenterDateRangeFilterComponent,
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.DATE_COLUMN,
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('filter.cdd'),
					suppressHeaderMenuButton: false,
					filterParams: { suppressAndOrCondition: true },
					cellRendererParams: { inputFormat: 'date', outputFormat: 'date' },
					valueFormatter: params => {
						const value = <Date | undefined> params.value;
						return value ? value.toLocaleDateString() : '';
					},
					valueGetter: params =>
						params.data?.cdd ? new Date(`${ params.data.cdd } 00:00:00`) : undefined
				})
			],
			[
				'customerOrderNumber',
				new CommonColDef<CallableOrderItem>({
					colId: 'customerOrderNumber',
					fields: ['customerOrderNumber', 'customerOrderItemNumber'],
					filterParams: { suppressAndOrCondition: true },
					filter: DatacenterOrderNumberFilterComponent,
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.ORDER_NUMBER_COLUMN,
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('filter.customerOrderNumber'),
					suppressHeaderMenuButton: false,
					valueGetter: params =>
						`${ params.data?.customerOrderNumber ?? '' }/${ params.data?.customerOrderItemNumber ?? '' }`
				})
			],
			[
				'soldTo',
				new CommonColDef<CallableOrderItem>({
					colId: 'soldTo',
					filter: DatacenterSuggestiveListFilterComponent,
					filterParams: {
						suppressAndOrCondition: true,
						batchFields: ['soldTo', 'soldToDescription'],
						url: GET_CALLABLE_BATCHES_URL
					},
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.sold-to'),
					headerComponent: CheckboxHeaderComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => params.data?.soldToDescription ?? ''
				})
			],
			[
				'dimensions',
				new CommonColDef<CallableOrderItem>({
					colId: 'dimensions',
					fields: ['thickness', 'width', 'length'],
					filter: DatacenterTextFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.DIMENSION_COLUMN,
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('order-tracking.dimension'),
					suppressHeaderMenuButton: false,
					valueGetter: params => this.dimensionPipe.transform(params.data)
				})
			],
			[
				'display',
				new IconMethodColDef<CallableOrderItem>({
					colId: 'display',
					filter: false,
					isMandatoryOnLeft: true,
					sortable: false,
					headerName: this.translationService.getTR('product.table.header.view'),
					maxWidth: 64,
					minWidth: 64,
					width: 64,
					headerComponent: CheckboxHeaderComponent,
					cellRendererParams: {
						icon: 'icon_view'
					},
					pinned: 'left',
					onCellClicked: (event: CellClickedEvent<CallableOrderItem>) => {
						const context = <{ onDisplay?: (item: CallableOrderItem) => void } | undefined> event.context;
						if (!context?.onDisplay || !event.node.data) {
							return;
						}
						context.onDisplay(event.node.data);
					}
				})
			],
			[
				'qtyBatchGross',
				new NumericColDef<CallableOrderItem>({
					colId: 'qtyBatchGross',
					filter: DatacenterNumberFilterComponent,
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.NUMBER_COLUMN,
					field: 'qtyBatchGross',
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('ecall-off.callable-weight'),
					suppressHeaderMenuButton: false
				})
			],
			[
				'steelgradeDescription',
				new CommonColDef<CallableOrderItem>({
					colId: 'steelgradeDescription',
					filter: DatacenterTextFilterComponent,
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					field: 'steelgradeDescription',
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('order.steelgrade'),
					suppressHeaderMenuButton: false
				})
			],
			[
				'supplierOrderNumber',
				new CommonColDef<CallableOrderItem>({
					colId: 'supplierOrderNumber',
					fields: ['supplierOrderNumber', 'supplierOrderItemNumber'],
					filter: DatacenterOrderNumberFilterComponent,
					filterParams: { suppressAndOrCondition: true },
					isMandatoryOnLeft: false,
					sortable: true,
					columnType: ColumnType.ORDER_NUMBER_COLUMN,
					headerComponent: CheckboxHeaderComponent,
					headerName: this.translationService.getTR('filter.supplierOrderNumber'),
					suppressHeaderMenuButton: false,
					valueGetter: params =>
						`${ params.data?.supplierOrderNumber ?? '' }/${ params.data?.supplierOrderItemNumber ?? '' }`
				})
			],
			[
				'serviceLevel',
				new CommonColDef<CallableOrderItem>({
					colId: 'serviceLevel',
					filter: DatacenterTextFilterComponent,
					isMandatoryOnLeft: false,
					sortable: true,
					headerName: this.translationService.getTR('datacenter-filter.service-level'),
					headerComponent: CheckboxHeaderComponent,
					field: 'serviceLevel',
					filterParams:
						{ suppressAndOrCondition: true, orderFields: ['serviceLevel', 'serviceLevelDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.serviceLevelDescription;
					},
				})
			]
		]);
	}
}

export const DEFAULT_VIEW_ECALL_BY_VOLUME_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'customerOrderNumber',
			pinned: 'left'
		},
		{
			colId: 'supplierOrderNumber'
		},
		{
			colId: 'steelgradeDescription'
		},
		{
			colId: 'dimensions'
		},
		{
			colId: 'qtyBatchGross'
		},
		{
			colId: 'cdd'
		},
		{
			colId: 'serviceLevel'
		}
	],
	sortModel: [
		{
			colId: 'cdd',
			sort: 'asc'
		}
	],
	filterState: [],
	type: ECALL_BY_VOLUME_DATACENTER_TYPE,
	isDefault: true,
	name: 'Default',
	id: -99
};
